export enum PriceWsAction {
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',
}

export enum PriceWsEvent {
  SUBSCRIBED = 'subscribed',
  UNSUBSCRIBED = 'unsubscribed',
  PRICE = 'price',
  ERROR = 'error',
}

export enum PriceWsCloseReason {
  InactiveTab = 'InactiveTab',
  NoConnectionRequired = 'NoConnectionRequired',
}

export const PRICE_WS_RECONNECT_ATTEMPTS = 5;
export const PRICE_WS_RECONNECT_INTERVAL_MS = 500;
export const PRICE_WS_MAX_HIDDEN_TAB_IDLE_TIME_MS = 60_000;
export const PRICE_WS_NORMAL_CLOSURE = 1000;

export interface PriceUpdate {
  ticker: string;
  exchange: string;
  price: number;
  time: string;
}

interface PricePayload extends PriceUpdate {
  event: PriceWsEvent.PRICE;
}

interface ErrorPayload {
  event: PriceWsEvent.ERROR;
  message: string;
}

interface SubscribedPayload {
  event: PriceWsEvent.SUBSCRIBED;
  ticker: string;
}

interface UnsubscribedPayload {
  event: PriceWsEvent.UNSUBSCRIBED;
  ticker: string;
}

export type SocketResponses =
  | PricePayload
  | SubscribedPayload
  | UnsubscribedPayload
  | ErrorPayload;

export type PriceSocketPayload = Partial<PriceUpdate> & {
  event: string;
  message?: string;
};

export type ErrorHandler = (message: string) => void;

export type UpdateHandler = (update: PriceUpdate) => void;

export type TickerPayload = {
  ticker: string;
  trading_country: string;
  exchange?: string;
};

export const getTickerKey = (ticker: TickerPayload) =>
  `${ticker.ticker}|${ticker.trading_country}|${ticker.exchange ?? ''}`;

export const buildPriceWsMessage = ({
  action,
  ticker,
  trading_country,
  exchange,
  delayedPrices = false,
}: TickerPayload & {
  action: PriceWsAction;
  delayedPrices?: boolean;
}) => {
  if (delayedPrices) {
    return JSON.stringify({
      action,
      ticker,
      exchange,
    });
  }

  return JSON.stringify({
    action,
    ticker,
    trading_country,
    exchange,
  });
};

export const parsePriceSocketPayload = (
  data: MessageEvent['data']
): PriceSocketPayload | undefined => {
  try {
    const payload: unknown = JSON.parse(String(data));

    if (
      !payload ||
      typeof payload !== 'object' ||
      !('event' in payload) ||
      typeof payload.event !== 'string'
    ) {
      return undefined;
    }

    return payload as PriceSocketPayload;
  } catch {
    return undefined;
  }
};

export const normalizePriceUpdate = (
  payload: Partial<PriceUpdate>
): PriceUpdate | undefined => {
  if (
    !payload.ticker ||
    payload.exchange === undefined ||
    payload.price === undefined
  ) {
    return undefined;
  }

  return {
    ticker: payload.ticker,
    exchange: payload.exchange,
    price: payload.price,
    time: payload.time ?? new Date().toISOString(),
  };
};

export const getLivePriceUpdate = (
  payload: PriceSocketPayload | undefined,
  ticker: string
): PriceUpdate | undefined => {
  if (
    !payload ||
    payload.event !== PriceWsEvent.PRICE ||
    payload.ticker !== ticker ||
    payload.exchange === undefined ||
    payload.price === undefined
  ) {
    return undefined;
  }

  // Do not filter by exchange: WS `exchange` is the trade venue (e.g. CBOE),
  // while callers typically pass the listing exchange (e.g. NASD).
  return normalizePriceUpdate(payload);
};
